import { Tooltip } from "@mui/material";
import ActionDownload from "assets/img/svg/actionDownload";
import BPSignedDocumentViewer from "components/shared/BPSignedDocumentViewer";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import { formatLocaleDateString } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { SignedDocument } from "store/articleStore";
import { downloadSignedDocument, fetchSignedDocumentContent, fetchTitleSignedAgreements } from "store/articleStore";
import type { RootState } from "store/store";
import { useAppDispatch } from "store/store";

import { buildDisplayName, typeLabel } from "./utils";

type TProps = {
    titleId: number;
};

const BPSignedDocumentsTable = ({ titleId }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const documents = useSelector((state: RootState) => state.article.titleSignedAgreements);
    const status = useSelector((state: RootState) => state.article.titleSignedAgreementsStatus);

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewedDoc, setViewedDoc] = useState<SignedDocument | null>(null);

    useEffect(() => {
        if (titleId) dispatch(fetchTitleSignedAgreements(titleId));
    }, [dispatch, titleId]);

    const rows = useMemo(
        () =>
            documents.map((doc) => ({
                ...doc,
                displayName: buildDisplayName(doc),
            })),
        [documents],
    );

    const resourceColumnLabel = (() => {
        const hasArticle = documents.some((d) => d.relatedResource === "article");
        const hasTerm = documents.some((d) => d.relatedResource === "term");
        if (hasTerm && !hasArticle) return t("BPTitle.SignedDocumentsTitle");
        if (hasArticle && !hasTerm) return t("BPTitle.SignedDocumentsArticle");
        return t("BPTitle.SignedDocumentsResource");
    })();

    const columns: LastElementTableColumns[] = [
        {
            id: "displayName",
            label: resourceColumnLabel,
            align: "left",
            sort: true,
        },
        {
            id: "type",
            label: t("BPTitle.SignedDocumentsType"),
            align: "left",
            format: (_value, _id, currentRow) => typeLabel(currentRow as SignedDocument, t),
            sort: true,
        },
        {
            id: "signedDate",
            label: t("BPTitle.SignedDocumentsDate"),
            align: "left",
            format: (value) =>
                value ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }) : "-",
            sort: true,
        },
        {
            id: "download",
            label: "",
            align: "right",
            minWidth: "60px",
            stopPropagation: true,
            format: (_value, _id, currentRow) => (
                <Tooltip title={t("ShowStatements.Download") as string}>
                    <span
                        style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            color: "#000",
                            padding: "4px",
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(downloadSignedDocument(currentRow as SignedDocument));
                        }}
                    >
                        <ActionDownload width="18" height="18" />
                    </span>
                </Tooltip>
            ),
        },
    ];

    const handleRowClick = (id: number) => {
        const doc = documents.find((d) => d.id === id);
        if (!doc) return;
        setViewedDoc(doc);
        setViewerOpen(true);
        dispatch(fetchSignedDocumentContent(doc.id));
    };

    return (
        <>
            <LastElementTable
                columns={columns}
                data={rows}
                onRowClick={handleRowClick}
                onRowClickId="id"
                max={-1}
                dataType="documents"
                isLoading={status === "pending"}
            />
            {viewedDoc && (
                <BPSignedDocumentViewer
                    open={viewerOpen}
                    title={viewedDoc.filename?.split(".")[0] ?? `Document_${viewedDoc.id}`}
                    onClose={() => setViewerOpen(false)}
                    onDownload={() => dispatch(downloadSignedDocument(viewedDoc))}
                />
            )}
        </>
    );
};

export default BPSignedDocumentsTable;
